import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import LoadingSpinner from "../Loader/load";

const OpenTable = () => {
  const { hash } = useParams();
  const [dataTable, setDataTable] = useState(null);
  const [clientName, setClientName] = useState(null);
  const [totalEquipments, setTotalEquipments] = useState(null);
  const [orderbyAnexo, setOrderbyAnexo] = useState("asc");
  const [orderbyRamal, setOrderbyRamal] = useState("asc");
  const [orderbyMov, setOrderbyMov] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  const sendEncryptedHash = async (hash) => {
    try {
      const response = await fetch("https://api-erp.voxcity.com.br/openTable", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ hash: encodeURIComponent(hash) }),
      });
      const data = await response.json();
      const { success, message, dados, nome, total } = data;
      if (success) {
        setDataTable(dados);
        setClientName(nome);
        setTotalEquipments(total);
      } else {
        console.error("Erro na requisição:", message);
      }
    } catch (error) {
      console.error("Erro na requisição:", error);
    }
  };

  useEffect(() => {
    if (!hash) return;
    sendEncryptedHash(hash);
  }, [hash]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!dataTable) return;
    
    const filtered = dataTable.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      return (
        item.nome_anexo?.toLowerCase().includes(searchLower) ||
        item.nome_modelo?.toLowerCase().includes(searchLower) ||
        item.netiqueta?.toLowerCase().includes(searchLower) ||
        item.nEtiqueta?.toLowerCase().includes(searchLower) ||
        item.nserie?.toLowerCase().includes(searchLower) ||
        item.nSerie?.toLowerCase().includes(searchLower) ||
        item.nmac?.toLowerCase().includes(searchLower) ||
        item.nMac?.toLowerCase().includes(searchLower) ||
        item.nome?.toLowerCase().includes(searchLower) ||
        item.setor?.toLowerCase().includes(searchLower) ||
        item.ramal?.toLowerCase().includes(searchLower) ||
        item.ip_estatico?.toLowerCase().includes(searchLower)
      );
    });
    
    setFilteredData(filtered);
  }, [dataTable, searchTerm]);

  const sortbyAnexo = () => {
    if (orderbyAnexo === "asc") {
      setDataTable(
        [...dataTable].sort((a, b) => a?.nome_anexo?.localeCompare(b?.nome_anexo))
      );
      setOrderbyAnexo("desc");
    } else {
      setDataTable(
        [...dataTable].sort((a, b) => b?.nome_anexo?.localeCompare(a?.nome_anexo))
      );
      setOrderbyAnexo("asc");
    }
  };

  const sortbyRamal = () => {
    if (orderbyRamal === "asc") {
      setDataTable([...dataTable].sort((a, b) => a?.ramal?.localeCompare(b?.ramal)));
      setOrderbyRamal("desc");
    } else {
      setDataTable([...dataTable].sort((a, b) => b?.ramal?.localeCompare(a?.ramal)));
      setOrderbyRamal("asc");
    }
  };

  const sortbyMov = () => {
    // Sem movimentação (null) sempre vai para o fim, nas duas direções
    const time = (item) =>
      item?.ultima_movimentacao ? new Date(item.ultima_movimentacao).getTime() : null;
    const direction = orderbyMov === "asc" ? 1 : -1;
    setDataTable(
      [...dataTable].sort((a, b) => {
        const ta = time(a);
        const tb = time(b);
        if (ta === null && tb === null) return 0;
        if (ta === null) return 1;
        if (tb === null) return -1;
        return (ta - tb) * direction;
      })
    );
    setOrderbyMov(orderbyMov === "asc" ? "desc" : "asc");
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return isNaN(date.getTime()) ? "—" : date.toLocaleDateString("pt-BR");
  };

  const getUniqueAnexos = () => {
    if (!dataTable) return [];
    return [...new Set(dataTable.map(item => item.nome_anexo))].filter(Boolean);
  };

  const getUniqueSetores = () => {
    if (!dataTable) return [];
    return [...new Set(dataTable.map(item => item.setor))].filter(Boolean);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const truncateText = (text, maxLength = 15) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header moderno */}
      <div className="bg-white shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Inventário de Equipamentos</h1>
                <p className="text-gray-600">Visualização em tempo real dos equipamentos</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Gerado em</p>
              <p className="text-sm font-medium text-gray-900">{new Date().toLocaleDateString('pt-BR')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Cards de estatísticas */}
        {clientName && (
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H7m2 0v-4a2 2 0 012-2h2a2 2 0 012 2v4" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Cliente</p>
                    <p className="text-xl font-semibold text-gray-900">{clientName}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total de Equipamentos</p>
                    <p className="text-xl font-semibold text-gray-900">{totalEquipments || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H7m2 0v-4a2 2 0 012-2h2a2 2 0 012 2v4" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Anexos</p>
                    <p className="text-xl font-semibold text-gray-900">{getUniqueAnexos().length}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Barra de pesquisa e filtros */}
        {dataTable && dataTable.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Pesquisar equipamentos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="text-sm text-gray-500 flex items-center">
                Mostrando {filteredData?.length || 0} de {dataTable?.length || 0} equipamentos
              </div>
            </div>
          </div>
        )}

        {/* Tabela moderna */}
        {dataTable && dataTable.length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed divide-y divide-gray-200">
                <colgroup>
                  <col className="w-12" />
                  <col className="w-48" />
                  <col className="w-32" />
                  <col className="w-28" />
                  <col className="w-36" />
                  <col className="w-36" />
                  <col className="w-48" />
                  <col className="w-32" />
                  <col className="w-20" />
                  <col className="w-32" />
                  <col className="w-32" />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th 
                      className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={sortbyAnexo}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Anexo</span>
                        <svg className={`h-4 w-4 transition-transform ${orderbyAnexo === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                        </svg>
                      </div>
                    </th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modelo</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patrimônio</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nº Série</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nº Mac</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Setor</th>
                    <th 
                      className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={sortbyRamal}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Ramal</span>
                        <svg className={`h-4 w-4 transition-transform ${orderbyRamal === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                        </svg>
                      </div>
                    </th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                    <th
                      className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={sortbyMov}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Últ. Movimentação</span>
                        <svg className={`h-4 w-4 transition-transform ${orderbyMov === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                        </svg>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData && filteredData.map((data, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 py-4 text-sm text-gray-500">
                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-100 text-xs font-medium text-gray-700">
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        <div className="flex items-center" title={data.nome_anexo}>
                          <div className="h-6 w-6 bg-blue-100 rounded-lg flex items-center justify-center mr-2 flex-shrink-0">
                            <svg className="h-3 w-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H7m2 0v-4a2 2 0 012-2h2a2 2 0 012 2v4" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900 cursor-help">
                            {truncateText(data.nome_anexo, 18)}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900 truncate">{data.nome_modelo}</td>
                      <td className="px-3 py-4">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {data.netiqueta || data.nEtiqueta}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900 font-mono truncate">{data.nserie || data.nSerie}</td>
                      <td className="px-3 py-4 text-sm text-gray-900 font-mono truncate">{data.nmac || data.nMac}</td>
                      <td className="px-3 py-4 text-sm font-medium text-gray-900 cursor-help" title={data.nome}>
                        {truncateText(data.nome, 20)}
                      </td>
                      <td className="px-3 py-4">
                        <span 
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 cursor-help"
                          title={data.setor}
                        >
                          {truncateText(data.setor, 12)}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900 font-mono">{data.ramal}</td>
                      <td className="px-3 py-4 text-sm text-gray-900 font-mono truncate">{data.ip_estatico}</td>
                      <td className="px-3 py-4 text-sm text-gray-900">{formatDate(data.ultima_movimentacao)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-500 text-lg">Carregando equipamentos...</p>
          </div>
        )}
      </div>

      {/* Botão Voltar ao Topo */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 ease-in-out transform hover:scale-110 z-50"
          aria-label="Voltar ao topo"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default OpenTable;
